import React, { useState, useEffect, useCallback, useRef } from 'react';

// Oyun sabitleri
const GAME_DURATION = 30; // saniye

// --- SES MOTORU ---
const audioCtx = new (window.AudioContext || window.webkitAudioContext)();

const playSound = (type) => {
    if (audioCtx.state === 'suspended') audioCtx.resume();

    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    const now = audioCtx.currentTime;

    if (type === 'hit') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);
        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
    } else if (type === 'miss') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.linearRampToValueAtTime(100, now + 0.2);
        gainNode.gain.setValueAtTime(0.2, now);
        gainNode.gain.linearRampToValueAtTime(0.01, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
    } else if (type === 'gameover') {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.linearRampToValueAtTime(200, now + 0.5);
        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.linearRampToValueAtTime(0, now + 0.5);
        osc.start(now);
        osc.stop(now + 0.5);
    }
};

// Stil nesneleri
const styles = {
    gameContainer: {
        width: '100vw',
        height: '100dvh', // Mobilde tam ekran (dinamik)
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#111827',
        fontFamily: "'Nunito', sans-serif",
        userSelect: 'none',
        overflow: 'hidden',
        touchAction: 'none',
    },
    header: {
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'clamp(1rem, 3vh, 1.5rem)', // Responsive padding
        zIndex: 20,
    },
    infoText: {
        fontSize: 'clamp(0.8rem, 2vh, 1rem)',
        color: '#9CA3AF',
        fontWeight: 'bold',
        letterSpacing: '1px',
    },
    valueText: {
        fontSize: 'clamp(1.5rem, 4vh, 2.25rem)',
        fontWeight: '900',
        color: '#FFFFFF',
        fontVariantNumeric: 'tabular-nums',
    },
    gameArea: {
        width: '100%',
        flexGrow: 1,
        backgroundColor: '#1F2937',
        position: 'relative',
        cursor: 'crosshair',
        touchAction: 'none',
        overflow: 'hidden',
    },
    modalOverlay: {
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        zIndex: 50,
    },
    modalContent: {
        textAlign: 'center',
        color: 'white',
        padding: '2rem',
        borderRadius: '1rem',
        animation: 'fadeIn 0.3s ease-out',
        maxWidth: '90%',
        width: '400px',
    },
    modalTitle: {
        fontSize: 'clamp(2rem, 5vw, 3rem)',
        fontWeight: '900',
        marginBottom: '1rem',
        background: 'linear-gradient(to right, #4ade80, #3b82f6)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    modalText: {
        fontSize: 'clamp(1rem, 3vw, 1.2rem)',
        color: '#D1D5DB',
        marginBottom: '2rem',
        lineHeight: 1.5,
    },
    finalScoreLabel: {
        fontSize: '1.2rem',
        marginBottom: '0.5rem',
        color: '#9CA3AF',
    },
    finalScore: {
        fontSize: 'clamp(3rem, 10vw, 5rem)',
        fontWeight: '900',
        marginBottom: '2rem',
        color: 'white',
    },
    button: {
        background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
        color: 'white',
        fontWeight: 'bold',
        padding: '1rem 3rem',
        borderRadius: '9999px',
        fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
        boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)',
        transition: 'transform 0.1s ease',
        border: 'none',
        cursor: 'pointer',
        width: '100%',
        maxWidth: '280px',
    }
};

// Ana Oyun Bileşeni
const Game = () => {
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
    const [gameState, setGameState] = useState('waiting');
    const [targets, setTargets] = useState([]);
    
    // Refs for game loop to avoid dependency closures
    const gameAreaRef = useRef(null);
    const scoreRef = useRef(0);
    const gameStateRef = useRef('waiting');
    const spawnTimeoutRef = useRef(null);
    const timerIntervalRef = useRef(null);

    // Score değiştiğinde ref'i güncelle (Loop içinde erişebilmek için)
    useEffect(() => {
        scoreRef.current = score;
    }, [score]);

    useEffect(() => {
        gameStateRef.current = gameState;
    }, [gameState]);

    const updateTimer = useCallback(() => {
        setTimeLeft(prev => {
            if (prev <= 1) {
                setGameState('gameOver');
                playSound('gameover');
                return 0;
            }
            return prev - 1;
        });
    }, []);

    // Hızlandırma Algoritması
    const getDifficultyParams = (currentScore) => {
        // Başlangıç hızı: 1000ms, En hızlı: 400ms
        // Her puan hızı ~20ms artırır
        const spawnDelay = Math.max(400, 1000 - (currentScore * 25));
        
        // Başlangıç ömrü: 2000ms, En kısa: 1000ms
        const lifespan = Math.max(1000, 2000 - (currentScore * 30));
        
        return { spawnDelay, lifespan };
    };

    const spawnLoop = useCallback(() => {
        if (gameStateRef.current !== 'playing') return;

        const gameArea = gameAreaRef.current;
        if (gameArea) {
            const currentScore = scoreRef.current;
            const { spawnDelay, lifespan } = getDifficultyParams(currentScore);

            const padding = 30; // Kenarlardan güvenli uzaklık
            const size = Math.random() * 40 + 50; // 50-90px
            
            const maxX = gameArea.clientWidth - size - padding;
            const maxY = gameArea.clientHeight - size - padding;

            const newTarget = {
                id: Date.now() + Math.random(),
                isGood: Math.random() > 0.25,
                size,
                x: Math.max(padding, Math.random() * maxX),
                y: Math.max(padding, Math.random() * maxY),
                lifespan: lifespan
            };

            setTargets(prev => [...prev, newTarget]);
            
            // Hedefi belirli bir süre sonra sil
            setTimeout(() => {
                setTargets(prev => prev.filter(t => t.id !== newTarget.id));
            }, lifespan);

            // Bir sonraki spawn için döngüyü kur
            spawnTimeoutRef.current = setTimeout(spawnLoop, spawnDelay);
        }
    }, []);

    useEffect(() => {
        if (gameState === 'playing') {
            timerIntervalRef.current = setInterval(updateTimer, 1000);
            spawnLoop(); // Döngüyü başlat
        }
        return () => {
            clearInterval(timerIntervalRef.current);
            clearTimeout(spawnTimeoutRef.current);
        };
    }, [gameState, updateTimer, spawnLoop]);

    useEffect(() => {
        if (gameState === 'gameOver') {
            setTargets([]);
        }
    }, [gameState]);

    const startGame = () => {
        setScore(0);
        scoreRef.current = 0;
        setTimeLeft(GAME_DURATION);
        setTargets([]);
        setGameState('playing');
        
        if (audioCtx.state === 'suspended') audioCtx.resume();
    };

    const handleTargetClick = (targetId, isGood) => {
        if (gameState !== 'playing') return;

        if (isGood) {
            playSound('hit');
            setScore(prev => prev + 1);
        } else {
            playSound('miss');
            setScore(prev => Math.max(0, prev - 2));
        }
        setTargets(prev => prev.filter(t => t.id !== targetId));
    };

    const handleMiss = (e) => {
        if (e.target === gameAreaRef.current && gameState === 'playing') {
            playSound('miss');
            setScore(prev => Math.max(0, prev - 1));
        }
    };

    return (
        <div style={styles.gameContainer}>
            <style>{`
                @keyframes fadeIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
            `}</style>

            <div style={styles.header}>
                <div>
                    <span style={styles.infoText}>SKOR</span>
                    <p style={styles.valueText}>{score}</p>
                </div>
                <div>
                    <span style={styles.infoText}>SÜRE</span>
                    <p style={{...styles.valueText, color: timeLeft <= 5 ? '#EF4444' : 'white'}}>{timeLeft}</p>
                </div>
            </div>

            <div
                ref={gameAreaRef}
                style={styles.gameArea}
                onPointerDown={gameState === 'playing' ? handleMiss : null}
            >
                {targets.map(target => (
                    <Target key={target.id} data={target} onClick={handleTargetClick} />
                ))}
            </div>

            {gameState !== 'playing' && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h2 style={styles.modalTitle}>
                            {gameState === 'waiting' ? 'Reflex Rush' : 'Süre Doldu!'}
                        </h2>
                        <p style={styles.modalText}>
                            {gameState === 'waiting' ? 'Yeşillere vur, Kırmızılardan kaçın! Hızlanmaya hazır ol.' : ''}
                        </p>
                        {gameState === 'gameOver' && (
                            <React.Fragment>
                                <p style={styles.finalScoreLabel}>TOPLAM SKOR</p>
                                <p style={styles.finalScore}>{score}</p>
                            </React.Fragment>
                        )}
                        <button 
                            style={styles.button}
                            onClick={startGame}
                            onPointerDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                            onPointerUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            onPointerLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            {gameState === 'waiting' ? 'BAŞLA' : 'TEKRAR OYNA'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Hedef Bileşeni
const Target = ({ data, onClick }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        requestAnimationFrame(() => setIsVisible(true));
    }, []);

    const handlePointerDown = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick(data.id, data.isGood);
    };

    const baseStyle = {
        position: 'absolute',
        borderRadius: '50%',
        transition: 'transform 0.1s ease-out', // Daha hızlı animasyon
        width: `${data.size}px`,
        height: `${data.size}px`,
        left: `${data.x}px`,
        top: `${data.y}px`,
        cursor: 'pointer',
        border: '3px solid rgba(255,255,255,0.2)',
        boxShadow: data.isGood 
            ? '0 0 15px rgba(34, 197, 94, 0.6)' 
            : '0 0 15px rgba(239, 68, 68, 0.6)',
        zIndex: 10,
        touchAction: 'none',
    };

    const typeStyle = {
        backgroundColor: data.isGood ? '#22C55E' : '#EF4444',
    };

    const visibilityStyle = {
        transform: isVisible ? 'scale(1)' : 'scale(0)',
    };

    // Dinamik lifespan'a göre animasyon süresi ayarlanabilir ama şimdilik sabit pop efekti yeterli
    const combinedStyle = { ...baseStyle, ...typeStyle, ...visibilityStyle };

    return (
        <div
            style={combinedStyle}
            onPointerDown={handlePointerDown}
        />
    );
};

export default function ReflexRush() {
  return <Game />;
}